import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { SideDrawerLayout } from "common/Components/SideDrawer";
import StatusPill from "common/Components/StatusPill";
import { GenericForm } from "common/Components/GenericForm";
import Toast from "common/Components/Toast";
import { FieldBlock, FieldHint } from "styles/formStyleComponents";
import CustomerAccessEditor from "./CustomerAccessEditor";
import {
  buildActiveCustomerSelectOptions,
  CUSTOMER_ACCESS_STATUS,
  DEFAULT_PLATFORM_ROLE_HINTS,
  enrichCustomerAccessRowFromActiveCustomer,
  getPlatformAccountStatusTone,
  hasCustomerAccessChanged,
  hasPlatformRoleChanged,
  isNonePlatformRole,
  isPlatformUserAccountEditable,
  normalizeCustomerAccessStatus,
  PLATFORM_USER_ACCESS_UI,
  isPlatformAdminPlatformRole,
  normalizePlatformRoleValue,
  shouldShowPlatformAdminOptionalCustomerHint,
  validatePlatformCustomerAccess,
} from "../PlatformUserAccess.constants";
import { userAccessDrawerPaperSx } from "../PlatformUserAccess.styles";
import { DetailTitleRow } from "./UserAccessDetailPanel.styles";
import { resolveRoleHint } from "pages/Admin/Users/Users.constants";

const createEmptyCustomerAccess = () => ({
  customerId: "",
  roles: [],
  accessStatus: "",
});

const buildUserAccessDetailFormSections = (
  platformRoleOptions,
  roleHint,
  canEdit
) => [
  {
    wrapper: FieldBlock,
    fields: [
      {
        name: "platformRole",
        type: "checkboxGroup",
        multiple: false,
        label: PLATFORM_USER_ACCESS_UI.invitePlatformRoleLabel,
        options: platformRoleOptions,
        disabled: !canEdit,
        labelId: "user-access-platform-role-label",
        hint: roleHint,
      },
    ],
  },
];

const mapRoleNamesToOptionValues = (roles, roleOptions) =>
  (roles ?? [])
    .map((role) => {
      const key = typeof role === "string" ? role : role?.value || role?.label;
      if (key == null || String(key).trim() === "") {
        return null;
      }
      const match = roleOptions.find(
        (option) =>
          String(option?.value) === String(key) ||
          String(option?.label) === String(key)
      );
      return match?.value ?? key;
    })
    .filter(Boolean);

const mapAccessToRow = (access, roleOptions = []) => ({
  accessId: access.accessId,
  customerId:
    access.customerId != null && access.customerId !== ""
      ? access.customerId
      : "",
  roles: mapRoleNamesToOptionValues(access.roles, roleOptions),
  accessStatus:
    normalizeCustomerAccessStatus(access.accessStatus ?? access.status) ||
    CUSTOMER_ACCESS_STATUS.ACTIVE,
  customerCode: access.customerCode,
  customerName: access.customerName,
});

const selectedUserSubtitle = (details) => {
  if (!details) return "";
  const fullName = [details.firstName, details.lastName]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(" ");
  if (fullName && details.email) {
    return `${fullName} — ${details.email}`;
  }
  return details.email ?? details.username ?? "";
};

const UserAccessDetailPanel = ({
  open,
  selectedUser,
  customers,
  platformRoleOptions,
  roleOptions,
  onSave,
  onCancel,
  onAccessActionSuccess,
  onCustomerUserListAction,
}) => {
  const [formValues, setFormValues] = useState({
    platformRole: PLATFORM_USER_ACCESS_UI.defaultPlatformRole,
  });
  const [customerAccessRows, setCustomerAccessRows] = useState([]);
  const [baseline, setBaseline] = useState({
    platformRole: PLATFORM_USER_ACCESS_UI.defaultPlatformRole,
    customerAccessRows: [],
  });
  const [validationError, setValidationError] = useState("");
  const [rowErrors, setRowErrors] = useState([]);
  const [saving, setSaving] = useState(false);

  const canEdit = isPlatformUserAccountEditable(selectedUser?.status);

  const customerOptions = useMemo(
    () =>
      buildActiveCustomerSelectOptions({
        customers,
        selectedAccessRows: customerAccessRows,
      }),
    [customers, customerAccessRows]
  );

  const platformRoles = useMemo(
    () =>
      (Array.isArray(platformRoleOptions) ? platformRoleOptions : []).map(
        (option) => ({
          value: String(option.value ?? option.id ?? option.label ?? ""),
          label: String(option.label ?? option.value ?? ""),
          hint: resolveRoleHint(option, DEFAULT_PLATFORM_ROLE_HINTS),
        })
      ),
    [platformRoleOptions]
  );

  useEffect(() => {
    if (!selectedUser) {
      return;
    }
    const platformRole =
      selectedUser.platformRole ?? PLATFORM_USER_ACCESS_UI.defaultPlatformRole;
    const rows = (selectedUser.customerAccess ?? [])
      .map((access) => mapAccessToRow(access, roleOptions))
      .map((row) => enrichCustomerAccessRowFromActiveCustomer(row, customers));
    setFormValues({ platformRole });
    setCustomerAccessRows(rows);
    setBaseline({ platformRole, customerAccessRows: rows });
    setValidationError("");
    setRowErrors([]);
  }, [selectedUser, customers, roleOptions]);

  const clearValidation = () => {
    setValidationError("");
    setRowErrors([]);
  };

  const normalizeRoleValues = (roles) =>
    (roles ?? []).map((role) =>
      typeof role === "string" ? role : role?.value || role?.label
    );

  const normalizeCustomerAccessRows = () =>
    customerAccessRows
      .filter((row) => row.customerId)
      .map((row) => {
        const enriched = enrichCustomerAccessRowFromActiveCustomer(
          row,
          customers
        );
        return {
          accessId: enriched.accessId,
          customerId: enriched.customerId,
          roles: normalizeRoleValues(enriched.roles).filter(Boolean),
          accessStatus:
            enriched.accessStatus ?? CUSTOMER_ACCESS_STATUS.ACTIVE,
          customerCode: enriched.customerCode,
          customerName: enriched.customerName,
        };
      });

  const platformRoleChanged = hasPlatformRoleChanged(
    baseline.platformRole,
    formValues.platformRole
  );
  const customerAccessChanged = hasCustomerAccessChanged(
    baseline.customerAccessRows,
    customerAccessRows
  );
  const hasChanges = platformRoleChanged || customerAccessChanged;
  const saveDisabled = !canEdit || !hasChanges || saving;
  const activePlatformRole = normalizePlatformRoleValue(formValues.platformRole);
  const showPlatformAdminOptionalHint =
    shouldShowPlatformAdminOptionalCustomerHint(activePlatformRole);
  const roleSummaryHint = isNonePlatformRole(formValues.platformRole)
    ? PLATFORM_USER_ACCESS_UI.inviteRoleSummaryNone
    : PLATFORM_USER_ACCESS_UI.inviteRoleSummaryPlatformAdmin;

  const handleSave = async () => {
    if (saveDisabled) {
      return;
    }
    const platformRole = normalizePlatformRoleValue(formValues.platformRole);
    const validation = validatePlatformCustomerAccess(
      customerAccessRows,
      platformRole
    );
    if (!validation.valid) {
      setValidationError(validation.formError ?? "");
      setRowErrors(validation.rowErrors);
      return;
    }
    clearValidation();
    setSaving(true);
    try {
      const success = await onSave({
        selectedUser,
        platformRoleChanged,
        customerAccessChanged,
        platformRole,
        customerAccessRows: normalizeCustomerAccessRows(),
      });
      if (success) {
        onCancel?.();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <SideDrawerLayout
      open={open}
      onClose={onCancel}
      title={
        <DetailTitleRow>
          <span>{PLATFORM_USER_ACCESS_UI.selectedUserTitle}</span>
          {selectedUser?.status ? (
            <StatusPill
              label={selectedUser.status}
              tone={getPlatformAccountStatusTone(selectedUser.status)}
            />
          ) : null}
        </DetailTitleRow>
      }
      titleId="user-access-detail-title"
      description={selectedUserSubtitle(selectedUser)}
      onSubmit={handleSave}
      submitLabel={PLATFORM_USER_ACCESS_UI.saveChangesLabel}
      cancelLabel={PLATFORM_USER_ACCESS_UI.cancelLabel}
      submitting={saving}
      submitDisabled={saveDisabled}
      paperSx={userAccessDrawerPaperSx}
    >
      {!canEdit ? (
        <FieldHint component="div">
          {PLATFORM_USER_ACCESS_UI.saveDisabledNotActiveHint}
        </FieldHint>
      ) : null}
      {validationError &&
      !isPlatformAdminPlatformRole(activePlatformRole) ? (
        <FieldHint component="div" role="alert" $error>
          {validationError}
        </FieldHint>
      ) : null}
      <GenericForm
        sections={buildUserAccessDetailFormSections(
          platformRoles,
          roleSummaryHint,
          canEdit
        )}
        values={{
          platformRole: normalizePlatformRoleValue(formValues.platformRole),
        }}
        onChange={(_name, nextRole) => {
          clearValidation();
          setFormValues((previous) => ({
            ...previous,
            platformRole: nextRole,
          }));
        }}
        showBorder={false}
      />
      <CustomerAccessEditor
          disabled={!canEdit}
          hideRemoveForPersistedRows
          rows={customerAccessRows}
          rowErrors={rowErrors}
          customerOptions={customerOptions}
          activeCustomers={customers}
          roleOptions={roleOptions}
          userId={selectedUser?.id}
          onAddRow={() => {
            clearValidation();
            setCustomerAccessRows((previous) => [
              ...previous,
              createEmptyCustomerAccess(),
            ]);
          }}
          onRemoveRow={(index) => {
            clearValidation();
            setCustomerAccessRows((previous) =>
              previous.filter((_, currentIndex) => currentIndex !== index)
            );
          }}
          onRowChange={(index, name, value) => {
            clearValidation();
            setCustomerAccessRows((previous) =>
              previous.map((row, currentIndex) => {
                if (currentIndex !== index) {
                  return row;
                }
                if (name === "customerId") {
                  const changed =
                    String(value ?? "") !== String(row.customerId ?? "");
                  const nextRow = changed
                    ? {
                        ...row,
                        customerId: value,
                        roles: [],
                        accessStatus: "",
                      }
                    : { ...row, customerId: value };
                  return enrichCustomerAccessRowFromActiveCustomer(
                    nextRow,
                    customers
                  );
                }
                return { ...row, [name]: value };
              })
            );
          }}
          onAccessActionSuccess={onAccessActionSuccess}
          onCustomerUserListAction={onCustomerUserListAction}
        />
    </SideDrawerLayout>
  );
};

UserAccessDetailPanel.propTypes = {
  open: PropTypes.bool,
  selectedUser: PropTypes.object,
  customers: PropTypes.arrayOf(PropTypes.object).isRequired,
  platformRoleOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  roleOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onAccessActionSuccess: PropTypes.func,
  onCustomerUserListAction: PropTypes.func,
};

UserAccessDetailPanel.defaultProps = {
  open: false,
};

export default UserAccessDetailPanel;
